/**
 * Contexte de la page publique en cours (cagnotte, adhésion, boutique,
 * événement, association) pour le chat IA : chaque page pousse un résumé
 * texte de ce qu'elle affiche déjà (titre, montants, dates…) ; le widget,
 * monté une seule fois pour tout le site (app.vue), le lit et le transmet à
 * l'IA — qui aide en priorité sur CETTE page, sauf si le visiteur change de
 * sujet (Karim 2026-10-12).
 *
 * `useState` : état partagé réactif entre la page et le widget global, sans
 * prop à faire remonter. Chaque page efface son contexte à son démontage,
 * pour ne pas le laisser traîner sur la page suivante.
 */
import { nomPaysDepuisCode, regleRecuFiscalPays } from "@donoor/utils";
import { useDemoAccueil } from "~/composables/useDemoAccueil";

export type TypeContextePage =
  | "cagnotte"
  | "adhesion"
  | "boutique"
  | "evenement"
  | "association";

export interface ContextePageChat {
  type: TypeContextePage;
  resume: string;
}

const CLE_ETAT = "donoor_chat_contexte_page";

export function useChatPageContext() {
  const contexte = useState<ContextePageChat | null>(CLE_ETAT, () => null);
  // Page affichée dans la démo de la home : le chat reste sur la HOME, il ne
  // prend pas le contexte de la démo.
  const { estIntegree } = useDemoAccueil();

  function definirContexte(type: TypeContextePage, resume: string): void {
    if (estIntegree) return;
    contexte.value = { type, resume };
  }

  function effacerContexte(): void {
    if (estIntegree) return;
    contexte.value = null;
  }

  return { contexte, definirContexte, effacerContexte };
}

/** Montant en euros, arrondi, lisible par l'IA (pas d'affichage visuel ici). */
export function euros(montant: number | null | undefined): string {
  if (montant == null || Number.isNaN(montant)) return "montant non précisé";
  return `${Math.round(montant).toLocaleString("fr-FR")} €`;
}

/** Date courte en français (« 12 octobre 2026 »), pour le résumé donné à l'IA. */
export function dateLisible(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

/**
 * Association hors France (chantier « autres pays ») : les faits de l'IA sur
 * les reçus fiscaux (Cerfa, 66 %) décrivent la France, la fiche lui dit donc
 * la règle du pays. France ou pays inconnu : null, rien ne change.
 *
 * Belgique et Luxembourg ont leurs propres justificatifs depuis le 30/10/2026,
 * les Pays-Bas depuis le 01/11/2026, l'Allemagne depuis le 03/11/2026 :
 * sans phrase, l'IA aurait répondu « 66 % » à un donateur belge.
 */
export function phrasePaysAssociation(pays: string | null | undefined): string | null {
  const code = (pays ?? "").trim().toUpperCase();
  if (!code) return null;
  const regime = regleRecuFiscalPays(code)?.regime ?? null;
  if (regime === "cerfa") return null;
  if (regime === "be_281_71") {
    return "Association située en Belgique : si elle délivre des attestations fiscales via Donoor, le donateur reçoit en janvier une attestation annuelle (fiche 281.71) pour ses dons de l'année à cette association, dès 40 € de dons, qui ouvre une réduction d'impôt de 30 %. Jamais de reçu Cerfa ni de réduction française de 66 % ; les billets, cotisations et achats ne sont pas déductibles.";
  }
  if (regime === "nl_anbi") {
    return "Association située aux Pays-Bas : si elle délivre des justificatifs via Donoor, c'est une ANBI (statut vérifié dans la liste publique du fisc néerlandais) ; le donateur reçoit un justificatif de don après chaque don et déduit ses dons de son revenu (giftenaftrek) pour la part de ses dons de l'année qui dépasse 1 % de son revenu (60 € au minimum), dans la limite de 10 %. Aucun reçu officiel n'est exigé aux Pays-Bas. Jamais de reçu Cerfa ni de réduction française de 66 % ; les billets, cotisations et achats ne sont pas déductibles.";
  }
  if (regime === "de_zuwendung") {
    return "Association située en Allemagne : si elle délivre des reçus via Donoor, le donateur reçoit par e-mail, après chaque don, une attestation de don (Zuwendungsbestätigung) au modèle officiel du ministère fédéral des Finances, en allemand, à son nom et à son adresse (tous deux obligatoires). Il déduit ses dons de son revenu imposable (Sonderausgaben, § 10b EStG), dans la limite de 20 % de son revenu : l'économie dépend de son taux d'imposition (au mieux 47,48 % du don, jamais garantie). Pour un don jusqu'à 300 €, un relevé bancaire suffit aussi au fisc. Jamais de reçu Cerfa ni de réduction française de 66 % ; Donoor n'établit ces attestations que pour les dons sans contrepartie (jamais pour un billet, une cotisation ou un achat).";
  }
  if (regime === "lu_art112") {
    return "Association située au Luxembourg : si elle délivre des reçus via Donoor, le donateur reçoit un reçu de don après chaque don ; ses dons sont déductibles de son revenu imposable dès 120 € par an à des organismes reconnus. Jamais de reçu Cerfa ni de réduction française de 66 % ; les billets, cotisations et achats ne sont pas déductibles.";
  }
  return `Association située en dehors de la France (${nomPaysDepuisCode(code, "fr")}) : Donoor n'y délivre pas de reçu fiscal (ni reçu Cerfa ni réduction d'impôt française) pour un don.`;
}

